import type { OccupancyLevel } from "@palestra/core";

/**
 * La larghezza della barra di capienza come classe, non come attributo `style`.
 *
 * La CSP di produzione dichiara `style-src 'self'` senza `'unsafe-inline'`, e
 * quella direttiva blocca anche gli **attributi** `style`: una larghezza scritta
 * li' sparisce nel deployment e l'elemento prende la larghezza automatica, cioe'
 * il 100% del contenitore — la barra appare piena per ogni slot. In sviluppo,
 * senza CSP, il difetto e' invisibile, che e' la ragione per cui e' sopravvissuto.
 *
 * Le larghezze vivono quindi in `styles.css`, una regola per gradino.
 */

/**
 * Il gradino della scala, in punti percentuali. Ogni gradino e' una regola in
 * piu' nel foglio di stile, che ha un budget di dimensione: dieci gradini lo
 * rispettano e bastano, perche' il numero esatto delle presenze e' gia' scritto
 * accanto alla barra — questa serve al colpo d'occhio.
 */
const STEP = 10;

/**
 * La classe di riempimento per `count` presenze su `capacity` posti.
 *
 * Due estremi sono esatti: 0% solo quando non c'e' nessuno, 100% solo quando lo
 * slot e' pieno o oltre soglia. Fra i due si arrotonda per difetto, cosi' la
 * barra non promette mai piu' presenze di quante ce ne siano — il coach non
 * legge «pieno» su uno slot che ha ancora un posto — con un gradino minimo che
 * tiene visibile la presenza singola in uno slot capiente.
 */
export function capacityFillClass(count: number, capacity: number): string {
  if (count <= 0 || capacity <= 0) return "capacity-fill-0";
  if (count >= capacity) return "capacity-fill-100";
  const step = Math.floor((count / capacity) * (100 / STEP)) * STEP;
  return `capacity-fill-${Math.min(100 - STEP, Math.max(STEP, step))}`;
}

/**
 * La tonalita' della scala di capienza, quando c'e' un livello da dire. Sta qui
 * con la barra: vista atleta e pannello coach — chunk diversi dopo PERF-04 —
 * devono dire lo stesso livello con la stessa classe.
 */
export function fillClass(level: OccupancyLevel | undefined): string {
  return level ? ` fill-${level}` : "";
}
